import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import {
  Button,
  Icons,
  SearchInput,
  useLayoutGutter,
  View,
  XStack,
} from '@usepepper/sprout-kit-ui';
import {
  countActiveUnpaidInvoiceFilters,
  UnpaidInvoicesFilterDialog,
} from '@finance-hub/domain/invoicing/components/unpaid-invoices-filter-dialog';
import {
  DEFAULT_UNPAID_INVOICE_FILTERS,
  UnpaidInvoiceSortField,
} from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-list';
import {
  DEFAULT_UNPAID_INVOICE_SORT,
  getNextUnpaidInvoiceSort,
} from '@finance-hub/domain/invoicing/blocs/utils/unpaid-invoice-sort-utils';
import { ErrorState } from '@finance-hub/components/error-state';
import { TableScrollHandle } from '@experiences/components/table/types';
import { UnpaidInvoicesBucketFilterChip } from '@finance-hub/domain/invoicing/components/unpaid-invoices-bucket-filter-chip';
import { UnpaidInvoicesTable } from '@finance-hub/domain/invoicing/components/unpaid-invoices-table';
import { UnpaidInvoicesTableEmptyState } from '@finance-hub/domain/invoicing/components/unpaid-invoices-table-empty-state';
import { UnpaidInvoicesTableSkeletonRows } from '@finance-hub/domain/invoicing/components/unpaid-invoices-table-skeleton-rows';
import { useDebouncedCallback } from 'use-debounce';
import { useSelectedBucketFilterLabel } from '@finance-hub/domain/invoicing/blocs/hooks/use-selected-bucket-filter-label';
import { useUnpaidInvoiceFilterStore } from '@finance-hub/domain/invoicing/blocs/stores/use-unpaid-invoice-filter-store';
import { useUnpaidInvoiceListLoader } from '@finance-hub/domain/invoicing/blocs/loaders/use-unpaid-invoice-list-loader';

const LOADING_SKELETON_ROW_COUNT = 8;
const LOADING_MORE_SKELETON_ROW_COUNT = 3;
const SEARCH_DEBOUNCE_MS = 300;

export const UnpaidInvoicesTableContainer: React.FC = () => {
  const [filters, setFilters] = useState(DEFAULT_UNPAID_INVOICE_FILTERS);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState(DEFAULT_UNPAID_INVOICE_SORT);
  const tableScrollHandleRef = useRef<TableScrollHandle | null>(null);
  const bucketFilter = useUnpaidInvoiceFilterStore(
    (state) => state.selectedBucketFilter,
  );
  const clearBucketFilter = useUnpaidInvoiceFilterStore(
    (state) => state.clearBucketFilter,
  );
  const bucketFilterLabel = useSelectedBucketFilterLabel();
  const gutter = useLayoutGutter();
  const { error, hasMore, loading, loadingMore, loadMore, refetch, rows } =
    useUnpaidInvoiceListLoader({ bucketFilter, filters, search, sort });

  const applySearch = useDebouncedCallback((text: string) => {
    setSearch(text);
    tableScrollHandleRef.current?.scrollToTop();
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    applySearch(text);
  };

  const flushSearch = () => applySearch.flush();

  const handleColumnPress = (field: UnpaidInvoiceSortField) => {
    setSort((current) => getNextUnpaidInvoiceSort(current, field));
    tableScrollHandleRef.current?.scrollToTop();
  };

  const activeFilterCount = countActiveUnpaidInvoiceFilters(filters);
  const hasActiveFilters = activeFilterCount > 0;

  if (error && rows.length === 0) {
    return (
      <ErrorState
        onRetry={refetch}
        title={i18n.t('finance_hub.invoices.table.error')}
      />
    );
  }

  return (
    <View flex={1} gap="$8">
      <XStack alignItems="center" gap="$8" paddingHorizontal={gutter}>
        {bucketFilterLabel ? (
          <UnpaidInvoicesBucketFilterChip
            label={bucketFilterLabel}
            onClear={clearBucketFilter}
          />
        ) : null}
        <SearchInput
          clearAriaLabel={i18n.t('clear_search')}
          onChangeText={handleSearchChange}
          onClear={flushSearch}
          onSubmit={flushSearch}
          placeholder={i18n.t('finance_hub.invoices.search_placeholder')}
          value={searchText}
          width="$244"
        />
        <Button
          alignSelf="center"
          icon={
            <Icons.ListFilter
              color={hasActiveFilters ? '$iconOnDark' : '$iconPrimary'}
              size="$16"
            />
          }
          onPress={() => setIsFilterDialogOpen(true)}
          size="$sm"
          variant={hasActiveFilters ? 'primary' : 'secondary'}
        >
          {hasActiveFilters
            ? i18n.t('filters_with_count', { count: activeFilterCount })
            : i18n.t('filters')}
        </Button>
      </XStack>
      <UnpaidInvoicesFilterDialog
        filters={filters}
        isOpen={isFilterDialogOpen}
        onApply={setFilters}
        onClose={() => setIsFilterDialogOpen(false)}
      />
      <UnpaidInvoicesTable
        emptyComponent={
          loading ? (
            <View flex={1} width="100%">
              <UnpaidInvoicesTableSkeletonRows
                count={LOADING_SKELETON_ROW_COUNT}
              />
            </View>
          ) : (
            <UnpaidInvoicesTableEmptyState />
          )
        }
        listFooter={
          loadingMore ? (
            <UnpaidInvoicesTableSkeletonRows
              count={LOADING_MORE_SKELETON_ROW_COUNT}
            />
          ) : null
        }
        onColumnPress={handleColumnPress}
        onEndReached={hasMore && !loading ? loadMore : undefined}
        rows={rows}
        scrollHandleRef={tableScrollHandleRef}
        sort={sort}
      />
    </View>
  );
};
